import { makeStyles, Paper, Typography } from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { LoadingDialog } from '../../../../components/LoadingDialog';
import { printAssetCategory } from '../../../../entities/AssetCategory';
import { LibraryItem } from '../../../../entities/LibraryItem';
import { LibraryItemType } from '../../../../entities/LibraryItemType';
import { LinkAssetType } from '../../../../entities/LinkAssetType';
import { routes } from '../../../../routes';
import { FileAssetService } from '../../../../services/FIleAssetService';
import DateHelper from '../../../../utilities/DateHelper';
import { orderBy } from '../../../../utilities/orderBy';
import { useLibrary } from '../../LibraryPage';
import { ShowEmbeddedLinkDialog } from './ShowEmbeddedLinkDialog';

const useStyles = makeStyles((theme) => ({
    root: {
        textAlign: 'left',
        paddingTop: theme.spacing(2),
        [theme.breakpoints.down('md')]: {
            padding: "0 0 8px 0"
        },

    },
    sectionTitle: {
        color: "#5f6368",
        fontSize: "16px",
        fontWeight: 500,
        fontFamily: "Roboto, Helvetica",
        borderBottom: "1px solid rgb(225,225,225)",
        paddingTop: "10px",
        paddingBottom: "10px",
        marginBottom: "8px",
        [theme.breakpoints.down('sm')]: {
            borderTop: "1px solid rgb(225,225,225)",
            backgroundColor: "#FFF",
            paddingLeft: "8px",
            fontSize: "13px",
        },
    },
    paperCards: {
        width: "220px",
        float: "left",
        height: "76px",
        borderRadius: "5px",
        border: "1px solid rgba(0,0,0,0.12)",
        boxShadow: "none",
        marginRight: "8px",
        marginBottom: "8px",
        padding: "10px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        cursor: "context-menu",
        [theme.breakpoints.down('md')]: {
            width: "220px",
            height: "100%",
            flexShrink: 0
        },
    },
    header: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        lineHeight: 1.25
    },
    date: {
        fontSize: "12px",
        lineHeight: "20px",
        color: "rgba(0,0,0,.54)",
        fontWeight: 500
    },
    quickCards: {
        [theme.breakpoints.down('md')]: {
            width: "100%",
            display: "flex",
            overflowX: "auto",
            overflowY: "hidden"
        },
    }
}));

export interface Props {
    focusedItemId?: string;
    onClick: (itemId: string) => void;
}

export function QuickAccess(props:Props) {
    const { focusedItemId } = props;
    const history = useHistory();
    const context = useLibrary();
    const classes = useStyles();

    const [downloading, setDownloading] = useState(false)
    const [showEmbeddedLinkDialog, setShowEmbeddedLinkDialog] = useState(false);
    const items = useMemo(() => 
        context.libraryItems.filter(i => i.type > 4).sort(orderBy.date(i => i.dateModified, "Descending")).slice(0, 5),
    [context.libraryItems])

    const focusedItem = useMemo(() => items.find(i => i.id === focusedItemId) , [focusedItemId, items]);

    const handleDoubleClick = async(item: LibraryItem) => {
        switch (item.type) {
			case LibraryItemType.BuilderAsset:
				return history.push(routes.app.resolve.builderPage(item.id), { prevUrl: window.location.href });

			case LibraryItemType.LinkAsset: {
				if (item.linkType! === LinkAssetType.General && item.linkUrl != null) {
					const redirectUrl = item.linkUrl.match(/^https?:\/\//i) ? item.linkUrl : "//" + item.linkUrl;
					return window.open(redirectUrl, "_blank");
				} else {
					return setShowEmbeddedLinkDialog(true)
				}
			}

			case LibraryItemType.FileAsset: {
                setDownloading(true);                
                await FileAssetService.downloadFile(item.id, item.name)
                setDownloading(false);   
                return;             
            }

			default:
                return history.push(routes.app.resolve.libraryFolder(item.id))
		}
    }

    return (
        <div className={classes.root}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Quick Access
            </Typography>
            <div className={classes.quickCards}>
                {items.slice(0, 5).map((item: LibraryItem) => {
                    return (
                        <Paper
                            key={item.id}
                            className={classes.paperCards}
                            elevation={1}
                            onDoubleClick={() => handleDoubleClick(item)}
                            style={{ backgroundColor: `${focusedItemId === item.id ? "#e8f0fe" : ""}` }}
                            onClick={() => props.onClick(item.id)}
                        >
                            <Typography
                                variant="subtitle1"
                                component="p"
                                style={{ color: `${focusedItemId === item.id ? "#1967d2" : ""}` }}
                                className={classes.header}

                            >
                                {item.name}
                            </Typography>
                            {item.category &&
                                <Typography component="p" className={classes.date}>
                                    {printAssetCategory(item.category)}
                                </Typography>
                            }
                            <Typography component="p" className={classes.date}>
                                {item.dateAccessed && `${DateHelper.buildDateFormat(item.dateAccessed)}`}
                            </Typography>
                        </Paper>
                    );
                })}
            </div>
            { showEmbeddedLinkDialog && focusedItem && focusedItem.type === LibraryItemType.LinkAsset &&
                <ShowEmbeddedLinkDialog 
                    open={showEmbeddedLinkDialog} 
                    onClose={() => setShowEmbeddedLinkDialog(false)}
                    linkUrl={focusedItem.linkUrl!}
                    linkType={focusedItem.linkType!}
                />
            }
            { downloading && <LoadingDialog text='Downloading' />}
        </div>
    )
}